/**
 * Research — read a web page onto the matter's record.
 *
 * What an attorney does with it: the examiner cites ARCTICA against the
 * client's ARCTIC; the attorney pastes the cited proprietor's "about" page, or
 * a register entry, or the press release about their funding round. A model
 * judges whether the page is about that party and what it establishes — in
 * use, not in use, different goods, a different channel — and which way it
 * cuts. The answer goes on the timeline with the URL as its provenance.
 *
 * What it refuses to do, and says so on the card:
 *
 * - it never lets a page instruct the model — a page that tried is shown as
 *   having tried, and rejected;
 * - it never turns a fact about *our own* client into a case fact — those are
 *   labelled as questions to put to the client, which is where I5 keeps them;
 * - it never softens a fact that cuts against us — that is the fact worth the
 *   most, because it stops a non-use plea against a company that just raised.
 *
 * The 503 is the one message worth rendering verbatim: a deployment with no
 * Firecrawl key is told where one goes, which is how the feature is discovered.
 */
import { useState, type FormEvent } from 'react'

import { errorMessage } from '@/api/client'
import {
  useMatterResearch,
  useResearchUrl,
  type ResearchCuration,
  type ResearchCuts,
  type ResearchFactType,
  type ResearchWhose,
} from '@/api/research'
import { useAuth } from '@/auth/AuthProvider'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  Select,
  useToast,
  type BadgeTone,
} from '@/components/ui'

const WHOSE_OPTION: Record<ResearchWhose, string> = {
  cited: 'the cited mark — the obstacle',
  ours: "our client's mark",
}

const WHOSE_SHORT: Record<ResearchWhose, string> = {
  cited: 'about the cited mark',
  ours: 'about our client',
}

const CUTS_LABEL: Record<ResearchCuts, string> = {
  for_us: 'helps us',
  against_us: 'cuts against us',
  neutral: 'neutral',
}

const CUTS_TONE: Record<ResearchCuts, BadgeTone> = {
  for_us: 'ok',
  against_us: 'block',
  neutral: 'neutral',
}

const FACT_TYPE_LABEL: Record<ResearchFactType, string> = {
  use: 'use of the mark',
  non_use: 'non-use',
  goods: 'the goods or services',
  channel: 'trade channel',
  proprietor_status: 'the proprietor',
  reputation: 'reputation',
  other: 'other',
}

export function ResearchCard({ matterId }: { matterId: string }) {
  const { hasScope } = useAuth()
  const canWrite = hasScope('matters:write')
  const research = useMatterResearch(matterId)
  const read = useResearchUrl(matterId)
  const toast = useToast()
  const [url, setUrl] = useState('')
  const [whose, setWhose] = useState<ResearchWhose>('cited')
  const [query, setQuery] = useState('')
  const [error, setError] = useState<string | null>(null)

  if (research.isPending) return null

  const rows = research.data ?? []

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      const result = await read.mutateAsync({ url: url.trim(), whose, query: query.trim() })
      setUrl('')
      setQuery('')
      if (result.verdict === 'accept') {
        toast.push(`Recorded — ${CUTS_LABEL[result.cuts]}`, {
          // An unhelpful fact is not a failure; it is the finding that stops a
          // bad plea. Never 'error' for it, and never 'success' either.
          tone: result.cuts === 'against_us' ? 'info' : 'success',
          description: result.fact,
        })
      } else {
        toast.push('Page rejected, and recorded as rejected', {
          tone: 'info',
          description: result.reason,
        })
      }
    } catch (caught) {
      setError(errorMessage(caught))
    }
  }

  return (
    <Card role="group" aria-label="Research" className="gap-3">
      <CardHeader
        title="Research"
        description="Read a web page — the cited proprietor's site, a register entry, a press release — and put what it establishes on the record. A model judges the page and the page never instructs it; a fact about our own client is a question to put to them, never something a draft asserts."
        actions={
          rows.length > 0 ? (
            <Badge tone="neutral">
              {rows.length} page{rows.length === 1 ? '' : 's'} read
            </Badge>
          ) : null
        }
      />

      {research.isError ? (
        // Not collapsed into "nothing read yet": a failed fetch and an empty
        // record send a reviewer somewhere different.
        <p className="m-0 text-micro text-muted">
          What has been read could not be loaded — {errorMessage(research.error)}
        </p>
      ) : null}

      {canWrite ? (
        <form className="flex flex-col gap-3" onSubmit={(event) => void onSubmit(event)}>
          <Field
            label="Page to read"
            hint="The full address. It is fetched by Firecrawl, not by this server."
          >
            {({ id, describedBy }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="url"
                inputMode="url"
                value={url}
                onChange={(event) => setUrl(event.target.value)}
                placeholder="https://"
                required
                disabled={read.isPending}
              />
            )}
          </Field>
          <div className="flex flex-wrap gap-3">
            <Field label="Whose mark is this page about" className="min-w-[16rem] flex-1">
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={whose}
                  onChange={(event) => setWhose(event.target.value as ResearchWhose)}
                  disabled={read.isPending}
                >
                  {(Object.keys(WHOSE_OPTION) as ResearchWhose[]).map((option) => (
                    <option key={option} value={option}>
                      {WHOSE_OPTION[option]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field
              label="Search that found it"
              hint="Optional. Context for the judgement, recorded with it."
              className="min-w-[16rem] flex-1"
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  maxLength={300}
                  disabled={read.isPending}
                />
              )}
            </Field>
          </div>
          {error ? (
            <p role="alert" className="m-0 text-micro text-block-fg">
              {error}
            </p>
          ) : null}
          <div>
            <Button
              type="submit"
              variant="secondary"
              disabled={read.isPending || url.trim().length === 0}
              loading={read.isPending}
              loadingLabel="Reading the page…"
            >
              Read this page
            </Button>
          </div>
        </form>
      ) : null}

      {rows.length === 0 ? (
        research.isError ? null : (
          <p className="m-0 text-body text-muted">Nothing has been read for this matter yet.</p>
        )
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {rows.map((row) => (
            <ResearchRow key={row.event_id} row={row} />
          ))}
        </ul>
      )}
    </Card>
  )
}

function ResearchRow({ row }: { row: ResearchCuration }) {
  const rejected = row.verdict !== 'accept'
  const meta = [
    row.dated ? `Dated ${row.dated}` : null,
    !rejected && row.fact_type !== 'other' ? `About ${FACT_TYPE_LABEL[row.fact_type]}` : null,
    `Judged by ${row.model_id || 'a model'}`,
    row.query ? `Search: ${row.query}` : null,
  ].filter((part): part is string => part !== null)

  return (
    <li className="flex flex-col gap-1 rounded-md border border-divider p-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <a
          href={row.url}
          target="_blank"
          rel="noreferrer noopener"
          className="text-body text-brand-700 no-underline hover:underline"
        >
          {row.title || row.url}
        </a>
        <Badge tone="outline">{WHOSE_SHORT[row.whose]}</Badge>
        {rejected ? (
          <Badge tone="neutral">
            {row.identity_confidence === 'unsure' ? 'rejected — not clearly this party' : 'rejected'}
          </Badge>
        ) : (
          <>
            <Badge tone={CUTS_TONE[row.cuts]}>{CUTS_LABEL[row.cuts]}</Badge>
            <Badge tone="outline">{row.strength}</Badge>
            {row.whose === 'ours' ? <Badge tone="warn">to confirm with the client</Badge> : null}
          </>
        )}
        {row.injection_flag ? <Badge tone="block">page tried to instruct the model</Badge> : null}
      </div>
      {row.fact ? <p className="m-0 text-body">{row.fact}</p> : null}
      {row.reason ? <p className="m-0 text-micro text-muted">{row.reason}</p> : null}
      <p className="m-0 text-micro text-muted">{meta.join(' · ')}</p>
    </li>
  )
}
